import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { AvatarFrame } from '../../shared/components/AvatarFrame.js';
import type { GameEndPlayer, GameEndView } from '../types.js';
import '../../shared/components/CastGrid.css';
import '../../discussion/components/CastGrid.css';

/**
 * The curtain call — the verso of the GameEnd spread. The whole table is
 * unmasked on the shared 3-column cast geometry: the winning side foregrounded,
 * the losing side recessed beneath a faint rule. Each cell reuses AvatarFrame +
 * the wrapper-agnostic [data-eliminated] Oxblood name-plate (the struck dead),
 * swapping the discussion's ClueSlip for a role-label caption (the unmask).
 * A revealed Ghost's role label sits in Spectral (--page-ghost-ink) — both
 * accents can appear on a struck ghost, but on separate elements.
 */

function CastCell({ player }: { player: GameEndPlayer }): JSX.Element {
  const { t } = useTranslation();
  return (
    <li>
      <span
        className="susdle-cast-cell"
        data-static="yes"
        data-eliminated={player.eliminated ? 'yes' : 'no'}
        data-role={player.role}
      >
        <AvatarFrame player={player} isHost={false} suppressEntry />
        <span className="susdle-gameend-role">{t(`gameEnd.role.${player.role}`)}</span>
      </span>
    </li>
  );
}

export function CurtainCast({ view }: { view: GameEndView }): JSX.Element {
  const { t } = useTranslation();
  const winners = view.cast.filter((p) => p.won);
  const losers = view.cast.filter((p) => !p.won);

  return (
    <div className="susdle-gameend-curtain">
      <ul className="susdle-cast-grid susdle-gameend-cast" aria-label={t('gameEnd.curtain.winnersAria')}>
        {winners.map((p) => (
          <CastCell key={p.id} player={p} />
        ))}
      </ul>

      {losers.length > 0 ? (
        <>
          <div className="susdle-gameend-curtain__rule" aria-hidden />
          <ul
            className="susdle-cast-grid susdle-gameend-cast susdle-gameend-cast--lose"
            aria-label={t('gameEnd.curtain.losersAria')}
          >
            {losers.map((p) => (
              <CastCell key={p.id} player={p} />
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
